import { Box, Flex, VStack } from "@chakra-ui/react";
import Menu from "components/Menu";
import Sidebar from "components/Sidebar";
import { ALL_LINKS } from "constants/permissions";
import { useFirebase } from "context/firebase.context";
import { useCallback, useEffect, useState, useMemo } from "react";
import _ from "lodash";

export default function MainLayout({ children, ...props }) {
  const { user } = useFirebase();
  const [claims, setClaims] = useState(null);
  const [isLoadingClaims, setIsLoadingClaims] = useState(true);

  const loadClaims = useCallback(async () => {
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult();
        setClaims(idTokenResult.claims);
      } catch (error) {
        console.error("Failed to load claims:", error);
      } finally {
        setIsLoadingClaims(false);
      }
    } else {
      setIsLoadingClaims(false);
    }
  }, [user]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  // Helper function to check if user has permission for a link
  // permission is an array of strings, e.g., ["operator"], ["analyst"], ["operator", "analyst"]
  // Array means OR logic - user needs at least one of the permissions
  const hasPermissionForLink = useCallback((permission, isOperator, isAnalyst) => {
    if (!permission || !Array.isArray(permission) || permission.length === 0) {
      return false;
    }

    // Check if user has at least one of the required permissions (OR logic)
    return permission.some((perm) => {
      if (perm === "operator") return isOperator;
      if (perm === "analyst") return isAnalyst;
      return false;
    });
  }, []);

  const links = useMemo(() => {
    if (isLoadingClaims || !claims) {
      return [];
    }

    const isOperator = _.get(claims, "operator", false);
    const isAnalyst = _.get(claims, "analyst", false);

    // Filter links based on permissions and hidden property
    return ALL_LINKS.filter((link) =>
      !link.hidden && hasPermissionForLink(link.permission, isOperator, isAnalyst)
    );
  }, [claims, isLoadingClaims, hasPermissionForLink]);

  return (
    <VStack minHeight="100vh" align="stretch" gap={0}>
      <Menu links={links} />
      <Flex flex={1}>
        <Sidebar links={links} />
        <Box flex={1}>
          <Box {...props}>{children}</Box>
        </Box>
      </Flex>
    </VStack>
  );
}
